import { createContext, useCallback, useContext, useState, ReactNode } from "react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { CreditWallModal } from "@/components/billing/CreditWallModal";
import { useInvalidateCreditStatus } from "@/hooks/useCreditStatus";

interface WallState {
  open: boolean;
  toolName?: string;
  cost?: number;
}

interface CreditWallContextValue {
  showWall: (opts?: { toolName?: string; cost?: number }) => void;
  /** Returns true if the error was an insufficient_credits 402 (and the modal was shown). */
  handleInvokeError: (err: unknown, toolName?: string) => Promise<boolean>;
}

const CreditWallContext = createContext<CreditWallContextValue | null>(null);

export function CreditWallProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WallState>({ open: false });
  const invalidate = useInvalidateCreditStatus();

  const showWall = useCallback((opts?: { toolName?: string; cost?: number }) => {
    setState({ open: true, toolName: opts?.toolName, cost: opts?.cost });
  }, []);

  const handleInvokeError = useCallback(
    async (err: unknown, toolName?: string): Promise<boolean> => {
      if (!(err instanceof FunctionsHttpError)) return false;
      try {
        const status = err.context?.status;
        if (status !== 402) return false;
        const body = await err.context.clone().json().catch(() => null);
        const code = body?.code ?? body?.error;
        if (code === "insufficient_credits") {
          setState({
            open: true,
            toolName,
            cost: body?.upgrade_context?.cost,
          });
          invalidate();
          return true;
        }
      } catch {
        /* swallow */
      }
      return false;
    },
    [invalidate],
  );

  return (
    <CreditWallContext.Provider value={{ showWall, handleInvokeError }}>
      {children}
      <CreditWallModal
        open={state.open}
        onOpenChange={(open) => setState((s) => ({ ...s, open }))}
        toolName={state.toolName}
        cost={state.cost}
      />
    </CreditWallContext.Provider>
  );
}

export function useCreditWall() {
  const ctx = useContext(CreditWallContext);
  if (!ctx) {
    // Soft fallback so callers in non-wrapped contexts don't crash.
    return {
      showWall: () => {},
      handleInvokeError: async () => false,
    } as CreditWallContextValue;
  }
  return ctx;
}
